import { Button, Grid, Heading } from '@design-systems/canvas-ui'
import { AvailabilityCard } from 'account/components/AvailabilityCard'
import { PreferencesCard } from 'account/components/PreferencesCard'
import { NotificationsCard } from 'account/components/NotificationsCard'
import { PartnerTypeCard } from 'account/components/PartnerTypeCard'
import { PartnerTypeLogoutModal } from 'account/components/PartnerTypeLogoutModal'
import ProfileInformationCard from 'account/components/ProfileInformationCard'
import { useForm, SubmitHandler } from 'react-hook-form'
import { AccountSettingsInputs } from 'account/types'
import { GetUserByIdResponse } from 'graphql/queries/users/queries/userQueries'
import {
  UPDATE_SPARK_USER,
  UpdateSparkUserInput,
  UPDATE_DEFAULT_PARTNER_ATTRIBUTES,
} from 'graphql/mutations/users/userMutations'
import { useMutation } from '@apollo/client/react'
import { useAuth } from '@praxis/component-auth'
import { useContext, useCallback, useState } from 'react'
import { UserContext } from 'App/UserContext'
import { useUnsavedChangesBlocker, UnsavedChangesModal } from '@plm-core/utils'

type AccountSettingsFormProps = {
  userData: GetUserByIdResponse['getById']
}

export const AccountSettingsForm = ({ userData }: AccountSettingsFormProps) => {
  const { sparkUser, setSparkUser } = useContext(UserContext)
  const { logout } = useAuth()

  const [updateSparkUser] = useMutation(UPDATE_SPARK_USER)
  const [updateDefaultPartnerAttributes] = useMutation(
    UPDATE_DEFAULT_PARTNER_ATTRIBUTES,
  )

  const [pendingFormData, setPendingFormData] =
    useState<AccountSettingsInputs | null>(null)
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const toFormValues = useCallback(
    (saved: typeof userData): AccountSettingsInputs => {
      const savedOptedOut =
        saved.notificationAttributes?.optedOutNotifications ?? []
      return {
        primaryRole: saved.primaryRole != null ? String(saved.primaryRole) : '',
        defaultPartnerType:
          saved.currentVMMRole != null ? String(saved.currentVMMRole) : '',
        betaTester: saved.betaTester ?? false,
        appearance:
          (saved.theme as AccountSettingsInputs['appearance']) ?? 'SYSTEM',
        notifyNewProductSamples: !savedOptedOut.includes(
          'PRODUCT_SAMPLES_CREATE',
        ),
        notifyCancelledProductSamples: !savedOptedOut.includes(
          'PRODUCT_SAMPLE_TRACKING_STATUS_CANCELLED',
        ),
        notifyClaimChanges: !savedOptedOut.includes('PRODUCT_CLAIM'),
        emailFrequency: saved.notificationAttributes?.optedBatching
          ? 'digest'
          : 'immediate',
        userOutOfOffice: saved.outOfOfficeAttributes?.userOutOfOffice ?? false,
        oooMessage: saved.outOfOfficeAttributes?.oooMessage ?? '',
        oooFromDate: saved.outOfOfficeAttributes?.oooFromDate ?? '',
        oooToDate: saved.outOfOfficeAttributes?.oooToDate ?? '',
      }
    },
    [],
  )

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting, isDirty, dirtyFields },
  } = useForm<AccountSettingsInputs>({
    defaultValues: toFormValues(userData),
  })

  const saveFormData = async (formData: AccountSettingsInputs) => {
    if (!sparkUser?.userProfileId) return

    const optedOutNotifications: string[] = []
    if (!formData.notifyNewProductSamples) {
      optedOutNotifications.push('PRODUCT_SAMPLES_CREATE')
    }
    if (!formData.notifyCancelledProductSamples) {
      optedOutNotifications.push('PRODUCT_SAMPLE_TRACKING_STATUS_CANCELLED')
    }
    if (!formData.notifyClaimChanges) {
      optedOutNotifications.push('PRODUCT_CLAIM')
    }

    const userUpdates: UpdateSparkUserInput = {
      primaryRole: formData.primaryRole ? Number(formData.primaryRole) : null,
      betaTester: formData.betaTester,
      theme: formData.appearance,
      notificationAttributes: {
        optedBatching: formData.emailFrequency === 'digest',
        optedOutNotifications,
      },
      outOfOfficeAttributes: {
        userOutOfOffice: formData.userOutOfOffice,
        oooMessage: formData.oooMessage || null,
        oooFromDate: formData.oooFromDate || null,
        oooToDate: formData.oooToDate || null,
      },
    }

    const result = await updateSparkUser({
      variables: {
        userProfileId: sparkUser.userProfileId,
        userUpdates,
      },
    })

    if (result.data?.updateSparkUser) {
      const updated = result.data.updateSparkUser
      reset(toFormValues(updated))
      setSparkUser((prev) =>
        prev
          ? {
              ...prev,
              theme: updated.theme ?? prev.theme,
              betaTester: updated.betaTester ?? prev.betaTester,
            }
          : prev,
      )
    }
  }

  const onSubmit: SubmitHandler<AccountSettingsInputs> = async (formData) => {
    if (!sparkUser?.userProfileId) return

    if (dirtyFields.defaultPartnerType) {
      setPendingFormData(formData)
      return
    }

    await saveFormData(formData)
  }

  const handleCancelPartnerTypeChange = () => {
    setPendingFormData(null)
  }

  const handleConfirmPartnerTypeChange = async () => {
    if (!pendingFormData || !sparkUser?.userProfileId) return

    const isPartnerTypeDirty = dirtyFields.defaultPartnerType
    const hasOtherChanges = Object.keys(dirtyFields).some(
      (key) => key !== 'defaultPartnerType',
    )

    setIsLoggingOut(true)
    try {
      if (hasOtherChanges) {
        await saveFormData(pendingFormData)
      }
      if (isPartnerTypeDirty) {
        await updateDefaultPartnerAttributes({
          variables: {
            userDefaultPartnerAttributes: {
              currentVMMRole: Number(pendingFormData.defaultPartnerType),
            },
          },
        })
      }
      localStorage.removeItem('userLoginState')
      logout()
    } finally {
      setIsLoggingOut(false)
      setPendingFormData(null)
    }
  }

  const blocker = useUnsavedChangesBlocker(isDirty)

  return (
    <Grid.Container justify="end">
      <Grid.Item size={{ xs: 12 }}>
        <Heading size={1}>Account settings</Heading>
        <div className="hc8-mv-4x">
          Manage your profile, availability, notifications, and preferences
        </div>
      </Grid.Item>
      <Grid.Item size={{ xs: 12 }}>
        <ProfileInformationCard user={userData} control={control} />
      </Grid.Item>
      <Grid.Item size={{ xs: 12 }}>
        <PartnerTypeCard control={control} />
      </Grid.Item>
      <Grid.Item size={{ xs: 12 }}>
        <AvailabilityCard control={control} setValue={setValue} />
      </Grid.Item>
      <Grid.Item size={{ xs: 12 }}>
        <NotificationsCard control={control} />
      </Grid.Item>
      <Grid.Item size={{ xs: 12 }}>
        <PreferencesCard control={control} />
      </Grid.Item>
      <Grid.Item>
        <Button
          variant="primary"
          isDisabled={!isDirty || isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          Save changes
        </Button>
      </Grid.Item>
      <UnsavedChangesModal {...blocker} />
      <PartnerTypeLogoutModal
        isOpen={!!pendingFormData}
        isLoggingOut={isLoggingOut}
        onConfirm={handleConfirmPartnerTypeChange}
        onCancel={handleCancelPartnerTypeChange}
      />
    </Grid.Container>
  )
}

export default AccountSettingsForm
